import { ClickAwayListener, Grow, MenuList, Paper, Popper } from '@mui/material'
import config from 'config'
import { KeyboardEvent } from 'react'
import MenuListItemButton from 'shared/components/MenuListItemButton'
import useToast from 'shared/components/Toast/useToast'

interface RosterExportBulkActionProps {
    rosterCandidateIds: string[]
    rosterId: string
    onClose: () => void
    isOpen: boolean
    anchorRef: React.RefObject<HTMLButtonElement>
}

export default function RosterExportBulkAction({
    rosterCandidateIds,
    rosterId,
    onClose,
    isOpen,
    anchorRef,
}: RosterExportBulkActionProps) {
    const toast = useToast()
    const handleClickOutside = () => {
        onClose()
    }

    const sendCandidateForResumeExport = async () => {
        try {
            const response = await fetch(`${config.api.uri}/exportResumes`, {
                method: 'POST',
                body: JSON.stringify({ rosterCandidateIds }),
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
            })

            const data = await response.json()
            if (response.ok) {
                handleClickOutside()
                toast({
                    title: 'Success',
                    message:
                        data.message ||
                        'Selected resumes being processed for download. Please stand by.',
                    severity: 'success',
                })
            } else {
                handleClickOutside()
                // Check if it's specifically a "no resumes" error
                const isNoResumesError =
                    data.message &&
                    data.message.includes('No resumes found for candidates')
                toast({
                    title: isNoResumesError
                        ? 'No Resumes Available'
                        : 'Something went wrong',
                    message:
                        data.message || 'Error registering candidate exports.',
                    severity: isNoResumesError ? 'warning' : 'error',
                })
            }
        } catch (error) {
            console.error('Failed to export candidate resumes', error)
            handleClickOutside()
            toast({
                title: 'Something went wrong',
                message: 'Please try again',
            })
        }
    }

    const sendCandidatesForExcelExport = async () => {
        try {
            const response = await fetch(
                `${config.api.uri}/exportCandidatesOnRoster`,
                {
                    method: 'POST',
                    body: JSON.stringify({ rosterCandidateIds, rosterId }),
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization:
                            'Bearer ' + localStorage.getItem('token'),
                    },
                }
            )

            const data = await response.json()
            if (response.ok) {
                handleClickOutside()
                toast({
                    title: 'Success',
                    message:
                        data.message ||
                        'Selected candidates are being processed for export. Please stand by.',
                    severity: 'success',
                })
            } else {
                handleClickOutside()
                toast({
                    title: 'Something went wrong',
                    message:
                        data.message || 'Error registering candidate exports.',
                })
            }
        } catch (error) {
            console.error('Failed to export candidates', error)
            handleClickOutside()
            toast({
                title: 'Something went wrong',
                message: 'Please try again',
            })
        }
    }

    return (
        <>
            <Popper
                sx={{ zIndex: 1 }}
                open={isOpen}
                anchorEl={anchorRef.current}
                transition
                disablePortal
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === 'bottom'
                                    ? 'center top'
                                    : 'center bottom',
                        }}
                    >
                        <Paper variant="outlined" sx={{ boxShadow: 1 }}>
                            <ClickAwayListener onClickAway={handleClickOutside}>
                                <MenuList
                                    id="roster-candidates-export-menu"
                                    disablePadding
                                    autoFocus
                                    onKeyDown={(e: KeyboardEvent) => {
                                        if (e.key === 'Escape')
                                            handleClickOutside()
                                    }}
                                >
                                    <MenuListItemButton
                                        title="Export Candidates to Excel"
                                        subtitle="Export selected candidates to Excel. Shown table columns will be included."
                                        onClick={sendCandidatesForExcelExport}
                                        borderBottom
                                    />
                                    <MenuListItemButton
                                        title="Export Candidate Resumes"
                                        subtitle="Export resumes for selected candidates. A zip file will be generated."
                                        onClick={sendCandidateForResumeExport}
                                    />
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    )
}
